<script setup lang="ts">
/* =============================================================
   APP LAYOUT
   =============================================================
   The signed-in shell: sidebar · top bar · content area.

   It owns exactly two pieces of UI state — whether the desktop
   sidebar is collapsed (persisted) and whether the mobile drawer
   is open (not persisted). Pages render into <RouterView> and
   know nothing about any of it.
   ============================================================= */

import { onUnmounted, ref, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import Sidebar from '@/components/shell/Sidebar.vue'
import Topbar from '@/components/shell/Topbar.vue'
import FullPageLoader from '@/components/ui/FullPageLoader.vue'
import { appConfig } from '@/config/app.config'
import { useAuth } from '@/lib/auth/useAuth'
import { useMediaQuery } from '@/lib/composables/useMediaQuery'

const MOBILE_QUERY = `(max-width: ${appConfig.ui.mobileBreakpoint - 1}px)`

function readCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(appConfig.storageKeys.sidebarCollapsed)
    if (stored !== null) return stored === 'true'
  } catch {
    // Storage unavailable — fall through to the configured default.
  }
  return appConfig.ui.sidebarCollapsedByDefault
}

const { user, signOut } = useAuth()
const route = useRoute()
const isMobile = useMediaQuery(MOBILE_QUERY)

const collapsed = ref(readCollapsed())
const drawerOpen = ref(false)
const signingOut = ref(false)

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  try {
    localStorage.setItem(appConfig.storageKeys.sidebarCollapsed, String(collapsed.value))
  } catch {
    // Not persisting is fine; the session still works.
  }
}

function closeDrawer() {
  drawerOpen.value = false
}

// Navigating on mobile should dismiss the drawer, and it must not
// linger when the viewport grows past the breakpoint.
watch(() => route.path, closeDrawer)
watch(isMobile, (mobile) => {
  if (!mobile) closeDrawer()
})

// Escape closes the drawer.
function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeDrawer()
}
document.addEventListener('keydown', onKeyDown)
onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown)
})

async function handleSignOut() {
  signingOut.value = true
  try {
    await signOut()
  } finally {
    signingOut.value = false
  }
}
</script>

<template>
  <!-- The router guard guarantees a user, but the types shouldn't assume it. -->
  <FullPageLoader v-if="!user" />

  <div v-else class="shell" :data-collapsed="collapsed && !isMobile">
    <a href="#main-content" class="skip-link">Skip to content</a>

    <Sidebar
      :role="user.role"
      :collapsed="collapsed"
      :drawer-open="drawerOpen"
      :is-mobile="isMobile"
      @toggle-collapsed="toggleCollapsed"
      @close-drawer="closeDrawer"
    />

    <div
      v-if="isMobile && drawerOpen"
      class="backdrop"
      role="presentation"
      data-testid="nav-backdrop"
      @click="closeDrawer"
    />

    <div class="main">
      <Topbar
        :user="user"
        :is-mobile="isMobile"
        :signing-out="signingOut"
        @open-drawer="drawerOpen = true"
        @sign-out="handleSignOut"
      />

      <main id="main-content" class="content">
        <div class="contentInner">
          <RouterView />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
  min-height: 100dvh;
  background-color: var(--color-bg);
}

/* Collapsed: one token change moves the whole grid. */
.shell[data-collapsed='true'] {
  --sidebar-width: var(--sidebar-width-collapsed);
}

.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.content {
  flex: 1;
  padding: var(--content-pad-y) var(--content-pad-x);
}

.contentInner {
  max-width: var(--content-max);
  margin-inline: auto;
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: calc(var(--z-overlay) - 1);
  background-color: rgb(4 18 29 / 45%);
  backdrop-filter: blur(2px);
  animation: fade var(--dur-fast) var(--ease);
}

@keyframes fade {
  from {
    opacity: 0;
  }
}

/* Below the breakpoint the sidebar leaves the grid and overlays. */
@media (max-width: 56.25rem) {
  .shell,
  .shell[data-collapsed='true'] {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
